import React, { useEffect, useState } from "react";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";

const APP_TIMEZONE = "Asia/Karachi";

const getBatchDisplayDate = (batch) => {
  if (!batch?.created_at) return "";
  return new Date(batch.created_at).toLocaleString("en-US", { timeZone: APP_TIMEZONE });
};

const getBatchDisplayName = (batch) => `Batch ${getBatchDisplayDate(batch)}`;

export default function AssignProperties() {
  const [circles, setCircles] = useState([]);
  const [circle, setCircle] = useState("");
  const [circleSearch, setCircleSearch] = useState("");
  const [showCircleDropdown, setShowCircleDropdown] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [properties, setProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [assignTo, setAssignTo] = useState("inspector"); // "inspector" or "supervisor" or "flagged_by_ai"
  // const [rank, setRank] = useState("");
  const [inspectorSearch, setInspectorSearch] = useState("");
  const [showInspectorDropdown, setShowInspectorDropdown] = useState(false);
  const [supervisorSearch, setSupervisorSearch] = useState("");
  const [showSupervisorDropdown, setShowSupervisorDropdown] = useState(false);
  const [inspectors, setInspectors] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [selectedInspectors, setSelectedInspectors] = useState([]);
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [showPropertiesList, setShowPropertiesList] = useState(false);

  // Load circles, batches & assignments
  useEffect(() => {
    axios.get("/properties/circles").then((res) => setCircles(res.data));
    axios.get("/properties/batches").then((res) => setBatches(res.data));
    loadAssignments();
  }, []);

  const loadAssignments = () => {
    axios.get("/properties/assignments").then((res) => setAssignments(res.data));
  };

  // Auto-load & auto-select properties on circle or batch change
  useEffect(() => {
    if (circle) {
      let url = `/properties/properties?circle=${circle}`;
      if (selectedBatch) {
        url += `&batchId=${selectedBatch}`;
      }
      axios.get(url).then((res) => {
        setProperties(res.data);
        setSelectedProperties(res.data.map((p) => p.id)); // auto-select all
        setShowPropertiesList(true);
      });
      // setInspectors([]);
      setSelectedInspectors([]);
      setInspectorSearch("");
      setSelectedSupervisors([]);
      setSupervisorSearch("");
    } else {
      setProperties([]);
      setSelectedProperties([]);
      setShowPropertiesList(false);
    }
  }, [circle, selectedBatch]);

  // Load inspectors on rank change
  // useEffect(() => {
  //   if (assignTo === "inspector" && rank) {
  //     axios.get(`/properties/inspectors?rank=${rank}`).then((res) => setInspectors(res.data));
  //     setSelectedInspectors([]);
  //   } else if (assignTo === "supervisor") {
  //     axios.get("/properties/supervisors").then((res) => setSupervisors(res.data));
  //     setSelectedSupervisors([]);
  //   } else if (assignTo === "flagged_by_ai" || assignTo === "flagged_by_supervisor") {
  //     // Load all inspectors for flagged by AI or flagged by supervisor options
  //     axios.get("/properties/inspectors").then((res) => setInspectors(res.data));
  //     setSelectedInspectors([]);
  //   }
  // }, [rank, assignTo]);

  useEffect(() => {
    if (assignTo === "inspector" || assignTo === "flagged_by_ai" || assignTo === "flagged_by_supervisor") {
      axios.get("/properties/inspectors").then((res) => setInspectors(res.data));
      setSelectedInspectors([]);
      setInspectorSearch("");
    } else if (assignTo === "supervisor") {
      axios.get("/properties/supervisors").then((res) => setSupervisors(res.data));
      setSelectedSupervisors([]);
      setSupervisorSearch("");
    }
  }, [assignTo]);

  // const toggleInspector = (id) => {
  //   setSelectedInspectors((prev) =>
  //     prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
  //   );
  // };

  const selectInspector = (id) => {
    setSelectedInspectors([id]);
  };

  // const toggleSupervisor = (id) => {
  //   setSelectedSupervisors((prev) =>
  //     prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
  //   );
  // };

  const selectSupervisor = (id) => {
    setSelectedSupervisors([id]);
  };

  const toggleProperty = (id) => {
    setSelectedProperties((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const toggleAllProperties = () => {
    if (selectedProperties.length === properties.length) {
      setSelectedProperties([]);
    } else {
      setSelectedProperties(properties.map((p) => p.id));
    }
  };

  const handleUnassign = async (circle, inspector) => {
    if (!window.confirm(`Unassign all properties in circle "${circle}"?`)) return;

    try {
      const res = await axios.delete(
        `/properties/unassign?circle=${encodeURIComponent(circle)}`
      );
      alert(res.data.message);
      loadAssignments();
    } catch (err) {
      console.error(err);
      if (err.response?.data?.error) {
        alert(err.response.data.error);
      } else {
        alert("Error connecting to server");
      }
    }
  };

  const handleUnassignBatch = async (batchId) => {
    if (!window.confirm(`Are you sure you want to unassign all properties in this batch?`)) return;

    try {
      const res = await axios.post("/properties/unassign-batch", { batchId });
      alert(res.data.message);
      axios.get("/properties/batches").then((res) => setBatches(res.data));
      loadAssignments();
    } catch (err) {
      console.error(err);
      alert("Error unassigning batch");
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm(`WARNING: This will delete all properties in this batch and unassign them. This action cannot be undone. Are you sure?`)) return;

    try {
      const res = await axios.delete(`/properties/batches/${batchId}`);
      alert(res.data.message);
      // Reload batches and assignments
      axios.get("/properties/batches").then((res) => setBatches(res.data));
      loadAssignments();
    } catch (err) {
      console.error(err);
      alert("Error deleting batch");
    }
  };

  const assignProperties = async () => {
    if (!selectedProperties.length) {
      alert("No properties selected to assign.");
      return;
    }

    if ((assignTo === "inspector" || assignTo === "flagged_by_ai" || assignTo === "flagged_by_supervisor") && !selectedInspectors.length) {
      alert("Select at least one inspector.");
      return;
    }

    if (assignTo === "supervisor" && !selectedSupervisors.length) {
      alert("Select at least one supervisor.");
      return;
    }

    setLoading(true);
    try {
      if (assignTo === "inspector") {
        await axios.post("/properties/assign-properties", {
          propertyIds: selectedProperties,
          inspectorIds: selectedInspectors,
        });
        alert(`Successfully assigned ${selectedProperties.length} properties to ${selectedInspectors.length} inspector(s)!`);
      } else if (assignTo === "supervisor") {
        await axios.post("/properties/assign-properties-supervisors", {
          propertyIds: selectedProperties,
          supervisorIds: selectedSupervisors,
        });
        alert(`Successfully assigned ${selectedProperties.length} properties to ${selectedSupervisors.length} supervisor(s)!`);
      } else if (assignTo === "flagged_by_ai") {
        // Assign each selected property to each selected inspector as flagged by AI
        const user = JSON.parse(localStorage.getItem("user"));
        for (const propertyId of selectedProperties) {
          for (const inspectorId of selectedInspectors) {
            await axios.post("/inspector/algorithm-flagged", {
              propertyId,
              inspectorId,
              flaggedReason: "Flagged by Admin via Bulk Assignment",
              flaggedBy: user.id
            });
          }
        }
        alert(`Successfully flagged ${selectedProperties.length} properties as AI-flagged for ${selectedInspectors.length} inspector(s)!`);
      } else if (assignTo === "flagged_by_supervisor") {
        // Assign each selected property to each selected inspector as flagged by supervisor
        const user = JSON.parse(localStorage.getItem("user"));
        for (const propertyId of selectedProperties) {
          for (const inspectorId of selectedInspectors) {
            await axios.post("/properties/flag", {
              propertyId,
              userId: user.id,
              comment: "Flagged by Admin via Bulk Assignment",
              inspectorId: inspectorId
            });
          }
        }
        alert(`Successfully flagged ${selectedProperties.length} properties as supervisor-flagged for ${selectedInspectors.length} inspector(s)!`);
      }

      const res = await axios.get(`/properties/properties?circle=${circle}`);
      setProperties(res.data);
      setSelectedProperties(res.data.map((p) => p.id));
      setSelectedInspectors([]);
      setSelectedSupervisors([]);
      setInspectorSearch("");
      setSupervisorSearch("");
      loadAssignments();
      axios.get("/properties/batches").then((res) => setBatches(res.data));
    } catch (err) {
      console.error(err);
      alert("Error assigning properties.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Sidebar />
      <div className="flex-1 bg-gray-50 p-6 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-2 tracking-tight">
              Assign Properties
            </h1>
            <p className="text-lg text-gray-600">
              Assign properties in a circle to multiple inspectors by group.
            </p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="xl:col-span-2 space-y-6">
              {/* Circle Selection */}
              {/* <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition">
                <h2 className="text-xl font-semibold text-gray-800 mb-3">
                  Select Circle
                </h2>
                <select
                  value={circle}
                  onChange={(e) => setCircle(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-gray-50 hover:bg-white"
                >
                  <option value="">-- Choose Circle --</option>
                  {circles.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div> */}
              {/* Circle Selection */}
              <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition relative">
                <h2 className="text-xl font-semibold text-gray-800 mb-3">
                  Select Circle
                </h2>
                <input
                  type="text"
                  value={circle ? circle : circleSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCircleSearch(value);
                    setCircle("");
                    setShowCircleDropdown(true);
                  }}
                  onFocus={() => {
                    setCircleSearch("");
                    setCircle("");
                    setShowCircleDropdown(true);
                  }}
                  onBlur={() => setTimeout(() => setShowCircleDropdown(false), 150)}
                  placeholder="Type to search circle..."
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-gray-50 hover:bg-white"
                />

                {showCircleDropdown && (
                  <div className="absolute z-20 left-6 right-6 bg-white border border-gray-200 rounded-xl mt-1 max-h-64 overflow-y-auto shadow-lg">
                    {circles
                      .filter((c) =>
                        c.toLowerCase().includes(circleSearch.toLowerCase())
                      )
                      .map((c) => (
                        <div
                          key={c}
                          onMouseDown={() => {
                            setCircle(c);
                            setCircleSearch("");
                            setShowCircleDropdown(false);
                          }}
                          className="px-4 py-2 cursor-pointer hover:bg-blue-50 text-gray-800"
                        >
                          {c}
                        </div>
                      ))}
                    {circles.filter((c) =>
                      c.toLowerCase().includes(circleSearch.toLowerCase())
                    ).length === 0 && (
                        <div className="px-4 py-2 text-gray-500">No matching circle</div>
                      )}
                  </div>
                )}
              </div>

              {/* Batch Selection */}
              {circle && (
                <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition">
                  <h2 className="text-xl font-semibold text-gray-800 mb-3">
                    Select Batch (Optional)
                  </h2>
                  <select
                    value={selectedBatch}
                    onChange={(e) => setSelectedBatch(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition bg-gray-50 hover:bg-white"
                  >
                    <option value="">-- All Batches --</option>
                    {batches
                      .filter((batch) => batch.unassigned_property_count > 0)
                      .map((batch) => (
                        <option key={batch.id} value={batch.id}>
                          {getBatchDisplayName(batch)} - {batch.unassigned_property_count} properties{batch.circles ? ` (${batch.circles})` : ""}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {/* Properties List - Shows properties of selected circle */}
              {circle && showPropertiesList && properties.length > 0 && (
                <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">
                      Properties in "{circle}" Circle
                    </h2>
                    <button
                      onClick={toggleAllProperties}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {selectedProperties.length === properties.length ? "Deselect All" : "Select All"}
                    </button>
                  </div>

                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <span className="font-bold">{selectedProperties.length}</span> of <span className="font-bold">{properties.length}</span> properties selected for assignment
                    </p>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr className="border-b border-gray-200">
                            <th className="py-2 px-3 text-left w-10">
                              <input
                                type="checkbox"
                                checked={selectedProperties.length === properties.length && properties.length > 0}
                                onChange={toggleAllProperties}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600"
                              />
                            </th>
                            <th className="py-2 px-3 text-left font-semibold text-gray-700">PIN</th>
                            <th className="py-2 px-3 text-left font-semibold text-gray-700">Location</th>
                            <th className="py-2 px-3 text-left font-semibold text-gray-700">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {properties.map((prop) => (
                            <tr key={prop.id} className="hover:bg-gray-50">
                              <td className="py-2 px-3">
                                <input
                                  type="checkbox"
                                  checked={selectedProperties.includes(prop.id)}
                                  onChange={() => toggleProperty(prop.id)}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600"
                                />
                              </td>
                              <td className="py-2 px-3 font-mono text-xs font-medium text-gray-800">
                                {prop.pin}
                              </td>
                              <td className="py-2 px-3 text-gray-600">
                                {prop.location || "—"}
                              </td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${prop.status === "resolved" ? "bg-green-100 text-green-700" :
                                  prop.status === "flagged" ? "bg-red-100 text-red-700" :
                                    prop.status === "new" ? "bg-blue-100 text-blue-700" :
                                      "bg-gray-100 text-gray-600"
                                  }`}>
                                  {prop.status || "—"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* No Properties Message */}
              {circle && properties.length === 0 && (
                <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100">
                  <div className="text-center py-8 text-gray-500">
                    No properties found in circle "{circle}"
                  </div>
                </div>
              )}

              {/* Assign To Selector */}
              {circle && properties.length > 0 && (
                <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition">
                  <h2 className="text-xl font-semibold text-gray-800 mb-3">
                    Assign To
                  </h2>
                  <select
                    value={assignTo}
                    onChange={(e) => setAssignTo(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-gray-50 hover:bg-white"
                  >
                    <option value="inspector">Inspector</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="flagged_by_ai">Flagged by AI</option>
                    <option value="flagged_by_supervisor">Flagged by Supervisor</option>
                  </select>
                </div>
              )}

              {/* Rank Selection */}
              {/* {circle && properties.length > 0 && assignTo === "inspector" && (
                <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition">
                  <h2 className="text-xl font-semibold text-gray-800 mb-3">
                    Select Inspector Group
                  </h2>
                  <select
                    value={rank}
                    onChange={(e) => setRank(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition bg-gray-50 hover:bg-white"
                  >
                    <option value="">-- Choose Group --</option>
                    <option value="4">Control</option>
                    <option value="1">Control Group</option>
                    <option value="2">Treatment (AI)</option>
                    <option value="3">Treatment (Supervisor)</option>
                    <option value="5">Treatment (Supervisor) New</option>
                    <option value="6">Treatment (Inspector6) New</option>
                    <option value="7">Treatment (Inspector7) New</option>
                    <option value="8">Control Group 2</option>
                    <option value="9">Control Group 3</option>
                  </select>
                </div>
              )} */}

              {/* Inspectors */}
              {/* {((assignTo === "inspector" && rank) || assignTo === "flagged_by_ai" || assignTo === "flagged_by_supervisor") && inspectors.length > 0 && (
                <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">
                      {assignTo === "flagged_by_ai" || assignTo === "flagged_by_supervisor" ? "Inspectors" : `Inspectors in Group ${rank}`}
                    </h2>
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {selectedInspectors.length} selected
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {inspectors.map((i) => (
                      <label
                        key={i.id}
                        className={`group flex items-center space-x-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${selectedInspectors.includes(i.id)
                          ? "border-blue-500 bg-blue-50 shadow-md"
                          : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white"
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedInspectors.includes(i.id)}
                          onChange={() => toggleInspector(i.id)}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 rounded border-2"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800 text-lg">
                            {i.name} ({i.username})
                          </div>
                          <div className="text-sm text-gray-500">
                            Circle: {i.circle || "N/A"}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-700">
                      ✅ <span className="font-bold">{selectedProperties.length}</span> selected properties from circle "{circle}" will be {assignTo === "flagged_by_ai" ? "flagged as AI-flagged for" : assignTo === "flagged_by_supervisor" ? "flagged by supervisor for" : "assigned to"} <strong>{selectedInspectors.length}</strong> selected inspector(s).
                    </p>
                  </div>
                </div>
              )} */}

              {/* Inspector - searchable single-select dropdown */}
              {(assignTo === "inspector" || assignTo === "flagged_by_ai" || assignTo === "flagged_by_supervisor") && inspectors.length > 0 && (
                <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition relative">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xl font-semibold text-gray-800">Select Inspector</h2>
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {selectedInspectors.length} selected
                    </span>
                  </div>

                  {(() => {
                    const selected = inspectors.find((i) => selectedInspectors.includes(i.id));
                    const selectedLabel = selected ? `${selected.name} (${selected.username})` : "";
                    return (
                      <input
                        type="text"
                        value={selected ? selectedLabel : inspectorSearch}
                        onChange={(e) => {
                          setInspectorSearch(e.target.value);
                          setSelectedInspectors([]);
                          setShowInspectorDropdown(true);
                        }}
                        onFocus={() => {
                          setInspectorSearch("");
                          setSelectedInspectors([]);
                          setShowInspectorDropdown(true);
                        }}
                        onBlur={() => setTimeout(() => setShowInspectorDropdown(false), 150)}
                        placeholder="Type to search inspector..."
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-gray-50 hover:bg-white"
                      />
                    );
                  })()}

                  {showInspectorDropdown && (
                    <div className="absolute z-20 left-6 right-6 bg-white border border-gray-200 rounded-xl mt-1 max-h-64 overflow-y-auto shadow-lg">
                      {inspectors
                        .filter((i) => `${i.name} ${i.username}`.toLowerCase().includes(inspectorSearch.toLowerCase()))
                        .map((i) => (
                          <div
                            key={i.id}
                            onMouseDown={() => {
                              selectInspector(i.id);
                              setInspectorSearch("");
                              setShowInspectorDropdown(false);
                            }}
                            className="px-4 py-2 cursor-pointer hover:bg-blue-50"
                          >
                            <div className="font-semibold text-gray-800">{i.name} ({i.username})</div>
                            <div className="text-sm text-gray-500">Circle: {i.circle || "N/A"}</div>
                          </div>
                        ))}
                      {inspectors.filter((i) =>
                        `${i.name} ${i.username}`.toLowerCase().includes(inspectorSearch.toLowerCase())
                      ).length === 0 && (
                          <div className="px-4 py-2 text-gray-500">No matching inspector</div>
                        )}
                    </div>
                  )}

                  {selectedInspectors.length > 0 && (
                    <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <p className="text-sm text-purple-700">
                        ✅ <span className="font-bold">{selectedProperties.length}</span> selected properties from circle "{circle}" will be {assignTo === "flagged_by_ai" ? "flagged as AI-flagged for" : assignTo === "flagged_by_supervisor" ? "flagged by supervisor for" : "assigned to"} the selected inspector.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Supervisors */}
              {/* {assignTo === "supervisor" && supervisors.length > 0 && (
                <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">
                      Supervisors
                    </h2>
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {selectedSupervisors.length} selected
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {supervisors.map((s) => (
                      <label
                        key={s.id}
                        className={`group flex items-center space-x-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${selectedSupervisors.includes(s.id)
                          ? "border-blue-500 bg-blue-50 shadow-md"
                          : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white"
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSupervisors.includes(s.id)}
                          onChange={() => toggleSupervisor(s.id)}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 rounded border-2"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800 text-lg">
                            {s.name} ({s.username})
                          </div>
                          <div className="text-sm text-gray-500">
                            Circle: {s.circle || "N/A"}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-700">
                      ✅ <span className="font-bold">{selectedProperties.length}</span> selected properties from circle "{circle}" will be assigned to <strong>{selectedSupervisors.length}</strong> selected supervisor(s).
                    </p>
                  </div>
                </div>
              )} */}

              {/* Supervisor - searchable single-select dropdown */}
              {assignTo === "supervisor" && supervisors.length > 0 && (
                <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition relative">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xl font-semibold text-gray-800">Select Supervisor</h2>
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {selectedSupervisors.length} selected
                    </span>
                  </div>

                  {(() => {
                    const selected = supervisors.find((s) => selectedSupervisors.includes(s.id));
                    const selectedLabel = selected ? `${selected.name} (${selected.username})` : "";
                    return (
                      <input
                        type="text"
                        value={selected ? selectedLabel : supervisorSearch}
                        onChange={(e) => {
                          setSupervisorSearch(e.target.value);
                          setSelectedSupervisors([]);
                          setShowSupervisorDropdown(true);
                        }}
                        onFocus={() => {
                          setSupervisorSearch("");
                          setSelectedSupervisors([]);
                          setShowSupervisorDropdown(true);
                        }}
                        onBlur={() => setTimeout(() => setShowSupervisorDropdown(false), 150)}
                        placeholder="Type to search supervisor..."
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-gray-50 hover:bg-white"
                      />
                    );
                  })()}

                  {showSupervisorDropdown && (
                    <div className="absolute z-20 left-6 right-6 bg-white border border-gray-200 rounded-xl mt-1 max-h-64 overflow-y-auto shadow-lg">
                      {supervisors
                        .filter((s) => `${s.name} ${s.username}`.toLowerCase().includes(supervisorSearch.toLowerCase()))
                        .map((s) => (
                          <div
                            key={s.id}
                            onMouseDown={() => {
                              selectSupervisor(s.id);
                              setSupervisorSearch("");
                              setShowSupervisorDropdown(false);
                            }}
                            className="px-4 py-2 cursor-pointer hover:bg-blue-50"
                          >
                            <div className="font-semibold text-gray-800">{s.name} ({s.username})</div>
                            <div className="text-sm text-gray-500">Circle: {s.circle || "N/A"}</div>
                          </div>
                        ))}
                      {supervisors.filter((s) =>
                        `${s.name} ${s.username}`.toLowerCase().includes(supervisorSearch.toLowerCase())
                      ).length === 0 && (
                          <div className="px-4 py-2 text-gray-500">No matching supervisor</div>
                        )}
                    </div>
                  )}

                  {selectedSupervisors.length > 0 && (
                    <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <p className="text-sm text-purple-700">
                        ✅ <span className="font-bold">{selectedProperties.length}</span> selected properties from circle "{circle}" will be assigned to the selected supervisor.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Assign Button */}
              {circle && properties.length > 0 && (
                <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100">
                  <button
                    onClick={assignProperties}
                    disabled={
                      loading ||
                      !selectedProperties.length ||
                      (assignTo === "inspector" && !selectedInspectors.length) ||
                      (assignTo === "supervisor" && !selectedSupervisors.length) ||
                      ((assignTo === "flagged_by_ai" || assignTo === "flagged_by_supervisor") && !selectedInspectors.length)
                    }
                    className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    {loading ? "Assigning..." : assignTo === "flagged_by_ai"
                      ? `Flag ${selectedProperties.length} Properties as AI-flagged for ${selectedInspectors.length} Inspector(s)`
                      : assignTo === "flagged_by_supervisor"
                        ? `Flag ${selectedProperties.length} Properties as Supervisor-flagged for ${selectedInspectors.length} Inspector(s)`
                        : `Assign ${selectedProperties.length} Properties to ${assignTo === "inspector" ? `${selectedInspectors.length} Inspector(s)` : `${selectedSupervisors.length} Supervisor(s)`}`}
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar Stats */}
            <div className="xl:col-span-1">
              <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-100 sticky top-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                  Quick Stats
                </h2>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4">
                    <div className="text-2xl font-bold text-blue-700">
                      {assignments.length}
                    </div>
                    <div className="text-sm text-blue-600">Total Assignments</div>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4">
                    <div className="text-2xl font-bold text-green-700">
                      {properties.length}
                    </div>
                    <div className="text-sm text-green-600">
                      Properties in {circle || "Selected Circle"}
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-4">
                    <div className="text-2xl font-bold text-purple-700">
                      {selectedInspectors.length}
                    </div>
                    <div className="text-sm text-purple-600">Inspectors Selected</div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-4">
                    <div className="text-2xl font-bold text-orange-700">
                      {selectedProperties.length}
                    </div>
                    <div className="text-sm text-orange-600">Properties Selected</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Batch Management */}
          <div className="mt-12 bg-white shadow-md rounded-2xl border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Batch Management</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Manage your uploaded batches
                </p>
              </div>
            </div>

            <div className="p-6">
              {batches.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No batches yet. Start by uploading properties.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Batch Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Uploaded By</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Property Count</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Upload Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Assigned To</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {batches.map((batch) => (
                        <tr key={batch.id} className="hover:bg-gray-50 transition">
                          <td className="py-3 px-4 font-medium text-gray-900">{getBatchDisplayName(batch)}</td>
                          <td className="py-3 px-4">{batch.uploaded_by_name || "Unknown"}</td>
                          <td className="py-3 px-4">
                            {batch.unassigned_property_count}
                            {batch.actual_property_count !== batch.unassigned_property_count && (
                              <span className="text-xs text-gray-400 ml-1">
                                / {batch.actual_property_count} total
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">{getBatchDisplayDate(batch)}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">
                            {batch.assigned_to && batch.assigned_to !== 'None' ? batch.assigned_to : '—'}
                          </td>
                          <td className="py-3 px-4 flex gap-2">
                            <button
                              onClick={() => handleUnassignBatch(batch.id)}
                              className="text-orange-600 hover:text-orange-800 text-sm font-medium bg-orange-50 hover:bg-orange-100 px-3 py-1 rounded-lg transition"
                            >
                              Unassign
                            </button>
                            <button
                              onClick={() => handleDeleteBatch(batch.id)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium bg-red-50 hover:bg-red-100 px-3 py-1 rounded-lg transition"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Current Assignments Table */}
          <div className="mt-12 bg-white shadow-md rounded-2xl border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Current Assignments</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {assignments.length} total assignments
                </p>
              </div>
            </div>

            <div className="p-6">
              {assignments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No assignments yet. Start by selecting a circle and assigning.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">
                          Total Assigned Properties
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">
                          Circle
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">
                          Inspector(s)
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {assignments.map((a, index) => (
                        <tr
                          key={`${a.circle}-${a.inspectors}`}
                          className={`hover:bg-gray-50 transition ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }`}
                        >
                          <td className="py-3 px-4 font-medium text-gray-900">
                            {a.total_assigned_properties}
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {a.circle}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-medium text-gray-900">
                            {a.inspectors}
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => handleUnassign(a.circle, a.inspectors)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium bg-red-50 hover:bg-red-100 px-3 py-1 rounded-lg transition"
                            >
                              Unassign
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}